import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { useOrg } from "../../lib/org";
import { Card, ErrorState, Money, PageHeader, Spinner } from "../../components/ui";
import { today } from "../../lib/format";
import { DateInput, ReportControls, ReportSectionTable } from "./controls";
import type { BalanceSheet as BS } from "@shared/types";

export default function BalanceSheet() {
  const { currentOrg } = useOrg();
  const currency = currentOrg?.baseCurrency ?? "USD";
  const [asOf, setAsOf] = useState(today());

  const { data, isLoading, error } = useQuery({
    queryKey: ["bs", currentOrg?.id, asOf],
    queryFn: () => api.get<BS>(`/reports/balance-sheet?asOf=${asOf}`),
    enabled: !!currentOrg,
  });

  return (
    <div>
      <PageHeader title="Balance Sheet" subtitle={`As of ${asOf}`} />
      <ReportControls>
        <DateInput label="As of" value={asOf} onChange={setAsOf} />
      </ReportControls>

      {isLoading && <Spinner />}
      {error && <ErrorState error={error} />}
      {data && (
        <Card className="p-6">
          <ReportSectionTable
            title="Assets"
            rows={data.assets.lines}
            total={data.assets.total}
            currency={currency}
          />
          <div className="mb-4 flex items-center justify-between border-t-2 border-gray-300 pt-2 text-base font-bold">
            <span>Total Assets</span>
            <Money cents={data.totalAssets} currency={currency} />
          </div>

          <ReportSectionTable
            title="Liabilities"
            rows={data.liabilities.lines}
            total={data.liabilities.total}
            currency={currency}
          />
          <ReportSectionTable
            title="Equity"
            rows={data.equity.lines}
            total={data.equity.total}
            currency={currency}
          />
          <div className="mt-2 flex items-center justify-between border-t-2 border-gray-300 pt-2 text-base font-bold">
            <span>Total Liabilities &amp; Equity</span>
            <Money cents={data.totalLiabilitiesAndEquity} currency={currency} />
          </div>

          {!data.balanced && (
            <p className="mt-4 rounded bg-red-50 px-3 py-2 text-sm text-red-600">
              ⚠ Balance sheet is out of balance by{" "}
              <Money
                cents={data.totalAssets - data.totalLiabilitiesAndEquity}
                currency={currency}
              />
              .
            </p>
          )}
        </Card>
      )}
    </div>
  );
}
